/**
 * Recommendations List Page
 *
 * Displays all recommendations grouped by priority:
 * - Immediate (high priority) - red/urgent styling
 * - Recommended (medium priority) - amber styling
 * - Optional (low priority) - gray styling
 *
 * Features:
 * - Summary header with counts per priority group
 * - Collapsible sections for each priority group
 * - Uses RecommendationCard component
 * - Loading skeletons and error states
 * - Empty state when no recommendations
 */

import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import {
  RecommendationCard,
  RecommendationCardSkeleton,
  type RecommendationCardData,
} from "@/components/recommendations";
import {
  useRecommendations,
  usePullToRefresh,
  getRecommendationsErrorMessage,
  type RecommendationApiData,
  type RecommendationCounts,
  type GroupedRecommendations,
} from "@/hooks";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  RefreshCw,
  Lightbulb,
  ChevronDown,
  AlertTriangle,
  Bell,
  Sparkles,
} from "lucide-react";
import { GlobalErrorFallback } from "@/components/error";

export const Route = createFileRoute("/recommendations/")({
  component: RecommendationsPage,
  errorComponent: GlobalErrorFallback,
});

// ============================================================================
// Types
// ============================================================================

type PriorityGroup = "immediate" | "recommended" | "optional";

interface PriorityGroupConfig {
  key: PriorityGroup;
  title: string;
  icon: React.ElementType;
  badgeClass: string;
  headerClass: string;
  countLabel: string;
}

// ============================================================================
// Constants
// ============================================================================

const PRIORITY_GROUP_CONFIG: PriorityGroupConfig[] = [
  {
    key: "immediate",
    title: "Immediate Action",
    icon: AlertTriangle,
    badgeClass:
      "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800",
    headerClass: "text-rose-700 dark:text-rose-400",
    countLabel: "Immediate",
  },
  {
    key: "recommended",
    title: "Recommended",
    icon: Bell,
    badgeClass:
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    headerClass: "text-amber-700 dark:text-amber-400",
    countLabel: "Recommended",
  },
  {
    key: "optional",
    title: "Optional",
    icon: Sparkles,
    badgeClass:
      "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    headerClass: "text-slate-600 dark:text-slate-400",
    countLabel: "Optional",
  },
];

// ============================================================================
// Data Transformation
// ============================================================================

/**
 * Transform API recommendation data to RecommendationCardData
 */
function transformToCardData(rec: RecommendationApiData): RecommendationCardData {
  return {
    id: rec.id,
    type: rec.type,
    priority: rec.priority,
    reason: rec.reason,
    currentValue: rec.currentValue,
    taxImpact: rec.taxImpact,
    fromFund: rec.fromFund,
    toFund: rec.toFund,
  };
}

// ============================================================================
// Summary Header Component
// ============================================================================

interface SummaryHeaderProps {
  counts: RecommendationCounts;
}

function SummaryHeader({ counts }: SummaryHeaderProps) {
  const parts = [];

  if (counts.immediate > 0) {
    parts.push(
      <span key="immediate" className="text-rose-600 dark:text-rose-400 font-medium">
        {counts.immediate} Immediate
      </span>
    );
  }

  if (counts.recommended > 0) {
    parts.push(
      <span key="recommended" className="text-amber-600 dark:text-amber-400 font-medium">
        {counts.recommended} Recommended
      </span>
    );
  }

  if (counts.optional > 0) {
    parts.push(
      <span key="optional" className="text-slate-600 dark:text-slate-400 font-medium">
        {counts.optional} Optional
      </span>
    );
  }

  if (parts.length === 0) {
    return null;
  }

  // Interleave with separators
  const elementsWithSeparators = parts.reduce<React.ReactNode[]>(
    (acc, part, index) => {
      if (index > 0) {
        acc.push(
          <span key={`sep-${index}`} className="text-muted-foreground mx-1">
            {" "}·{" "}
          </span>
        );
      }
      acc.push(part);
      return acc;
    },
    []
  );

  return (
    <div className="flex items-center gap-2 text-sm">{elementsWithSeparators}</div>
  );
}

// ============================================================================
// Collapsible Section Component
// ============================================================================

interface CollapsibleSectionProps {
  config: PriorityGroupConfig;
  recommendations: RecommendationApiData[];
  onRecommendationClick: (id: string) => void;
  defaultOpen?: boolean;
}

function CollapsibleSection({
  config,
  recommendations,
  onRecommendationClick,
  defaultOpen = true,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const Icon = config.icon;

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", config.headerClass)} />
          <h2 className={cn("font-semibold text-lg", config.headerClass)}>
            {config.title}
          </h2>
          <Badge variant="outline" className={cn("ml-2", config.badgeClass)}>
            {recommendations.length}
          </Badge>
        </div>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="space-y-3">
          {recommendations.map((rec) => (
            <RecommendationCard
              key={rec.id}
              data={transformToCardData(rec)}
              onClick={onRecommendationClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Skeleton Components
// ============================================================================

function SummaryHeaderSkeleton() {
  return (
    <div className="flex items-center gap-2">
      <Skeleton className="h-5 w-20" />
      <Skeleton className="h-5 w-4" />
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-5 w-4" />
      <Skeleton className="h-5 w-16" />
    </div>
  );
}

function CollapsibleSectionSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
        <Skeleton className="h-5 w-5 rounded" />
      </div>

      {/* Cards Skeleton */}
      <div className="space-y-3">
        {[...Array(count)].map((_, i) => (
          <RecommendationCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function RecommendationsPageSkeleton() {
  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      {/* Header Skeleton */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-56" />
            <div className="mt-2">
              <SummaryHeaderSkeleton />
            </div>
          </div>
          <Skeleton className="h-9 w-9" />
        </div>
      </div>

      {/* Sections Skeleton */}
      <div className="space-y-6">
        <CollapsibleSectionSkeleton count={2} />
        <CollapsibleSectionSkeleton count={3} />
        <CollapsibleSectionSkeleton count={1} />
      </div>
    </div>
  );
}

// ============================================================================
// Error Component
// ============================================================================

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
}

function ErrorState({ message, onRetry, isRetrying }: ErrorStateProps) {
  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
        <h3 className="mt-4 text-lg font-semibold text-destructive">
          Failed to load recommendations
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <Button
          variant="outline"
          onClick={onRetry}
          disabled={isRetrying}
          className="mt-4"
        >
          {isRetrying ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Empty State Component
// ============================================================================

function EmptyState() {
  return (
    <Card className="py-12">
      <CardContent className="text-center">
        <Lightbulb className="mx-auto h-16 w-16 text-muted-foreground" />
        <h3 className="mt-4 text-xl font-semibold text-foreground">
          No recommendations right now
        </h3>
        <p className="mt-2 text-muted-foreground max-w-md mx-auto">
          Your portfolio looks well-optimized. We'll notify you when there are
          new recommendations to improve your investments.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function RecommendationsPage() {
  const navigate = useNavigate();

  // Fetch recommendations data
  const {
    groupedRecommendations,
    counts,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useRecommendations();

  // Pull to refresh
  const { isRefreshing, handleRefresh } = usePullToRefresh({
    onRefresh: refetch,
    isFetching,
  });

  // Navigate to recommendation detail
  const handleRecommendationClick = (id: string) => {
    navigate({ to: "/recommendations/$recommendationId", params: { recommendationId: id } });
  };

  // Show skeleton while loading
  if (isLoading) {
    return <RecommendationsPageSkeleton />;
  }

  // Show error state
  if (isError) {
    return (
      <ErrorState
        message={getRecommendationsErrorMessage(error)}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    );
  }

  // Check if there are no recommendations
  const hasRecommendations = counts.total > 0;

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      className="h-full"
    >
      <div className="container mx-auto max-w-3xl px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Smart Recommendations
              </h1>
              {hasRecommendations && (
                <div className="mt-2">
                  <SummaryHeader counts={counts} />
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
            >
              <RefreshCw
                className={cn("h-4 w-4", (isRefreshing || isFetching) && "animate-spin")}
              />
            </Button>
          </div>
        </div>

        {/* Content */}
        {!hasRecommendations ? (
          <EmptyState />
        ) : (
          <div className="space-y-6">
            {PRIORITY_GROUP_CONFIG.map((config) => (
              <CollapsibleSection
                key={config.key}
                config={config}
                recommendations={groupedRecommendations[config.key]}
                onRecommendationClick={handleRecommendationClick}
                defaultOpen={true}
              />
            ))}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
